/**
 * Деньги кассы: два плюса, замок ноги, доля по ногам, азиат.
 * Зеркало backend/forq/book/pnl.py. Расхождение = «касса врёт».
 */

import { Decimal } from 'decimal.js';

import { roundAndRebalance } from '../lib/calc';

Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

const ZERO = new Decimal(0);
const ONE = new Decimal(1);
const QUARTER = new Decimal('0.25');
const CENT = new Decimal('0.01');

const WIN_RATE_SKIP = new Set([
  'void',
  'push',
  'half_win',
  'half_loss',
  'pending',
  'rejected',
  'gave_up',
]);

export type Money = Decimal | string | number;

function d(value: Money): Decimal {
  if (value instanceof Decimal) return value;
  return new Decimal(value);
}

function pick(seq: readonly Money[] | undefined, i: number, missing: Decimal): Decimal {
  if (seq === undefined || i >= seq.length) return missing;
  return d(seq[i]!);
}

function rub(amount: Money, fx: Decimal): Decimal {
  return d(amount).times(fx).toDecimalPlaces(2);
}

export function leftoverPair(
  stakes: readonly Money[],
  odds: readonly Money[],
  fees?: readonly Money[],
  fx?: readonly Money[],
): [Decimal, Decimal] {
  if (stakes.length < 2 || odds.length < 2) throw new Error('для пары плюсов нужны две ноги');
  const stakeR = stakes.map((s, i) => rub(s, pick(fx, i, ONE)));
  const feeR = stakes.map((_, i) => rub(pick(fees, i, ZERO), pick(fx, i, ONE)));
  const total = stakeR.reduce((a, b) => a.plus(b), ZERO);
  const first = stakeR[0]!.times(d(odds[0]!)).minus(feeR[0]!).minus(total).toDecimalPlaces(2);
  const second = stakeR[1]!.times(d(odds[1]!)).minus(feeR[1]!).minus(total).toDecimalPlaces(2);
  return [first, second];
}

export function lockRebalance(
  fillStake: Money,
  fillIndex: number,
  odds: readonly Money[],
  step: Money = 1,
): { stakes: Decimal[]; pair: [Decimal, Decimal] } {
  const fill = d(fillStake);
  const odd = d(odds[fillIndex]!);
  if (fill.lte(0) || odd.lte(0)) throw new Error('фил и кэф должны быть больше нуля');
  const bank = fill.times(odd);
  const plan = roundAndRebalance(bank, odds.map(String), d(step), fillIndex);
  const next = [...plan.stakes];
  next[fillIndex] = fill;
  return { stakes: next, pair: leftoverPair(next, odds) };
}

function legResult(stake: Decimal, odd: Decimal, fee: Decimal, outcome: string): Decimal {
  if (outcome === 'won') return stake.times(odd).minus(fee).minus(stake);
  if (outcome === 'lost') return stake.neg();
  if (outcome === 'void' || outcome === 'push' || outcome === 'pending' || outcome === 'gave_up' || outcome === 'rejected') {
    return ZERO;
  }
  if (outcome === 'half_win') {
    return stake.div(2).times(odd.minus(1)).minus(fee.div(2)).toDecimalPlaces(2);
  }
  if (outcome === 'half_loss') return stake.div(2).neg().toDecimalPlaces(2);
  return ZERO;
}

function nakedShare(
  stake: Decimal[],
  outcomes: readonly string[],
  odds: readonly Money[],
  fee: Decimal[],
): { shares: Decimal[]; total: Decimal } {
  const shares = [ZERO, ZERO];
  for (let i = 0; i < outcomes.length; i += 1) {
    const outcome = outcomes[i]!;
    if (outcome === 'pending' || outcome === 'rejected' || outcome === 'gave_up') continue;
    shares[i] = legResult(stake[i]!, d(odds[i]!), fee[i]!, outcome).toDecimalPlaces(2);
  }
  return { shares, total: shares.reduce((a, b) => a.plus(b), ZERO) };
}

export function legPnlShare(
  stakes: readonly Money[],
  outcomes: readonly string[],
  odds: readonly Money[],
  fees?: readonly Money[],
): { shares: Decimal[]; total: Decimal } {
  if (stakes.length !== outcomes.length || stakes.length !== odds.length) {
    throw new Error('ноги, исходы и кэфы разной длины');
  }
  const stake = stakes.map(d);
  const fee = stakes.map((_, i) => pick(fees, i, ZERO));
  const decided = outcomes.filter((o) => o !== 'pending' && o !== 'rejected' && o !== 'gave_up');
  if (decided.length === 1 && outcomes.length === 2) {
    return nakedShare(stake, outcomes, odds, fee);
  }
  const raw = outcomes.map((o, i) => legResult(stake[i]!, d(odds[i]!), fee[i]!, o));
  if (outcomes.some((o) => o === 'half_win' || o === 'half_loss' || o === 'void' || o === 'push')) {
    const shares = raw.map((p) => p.toDecimalPlaces(2));
    return { shares, total: shares.reduce((a, b) => a.plus(b), ZERO) };
  }
  let total = raw.reduce((a, b) => a.plus(b), ZERO).toDecimalPlaces(2);
  const lost = outcomes.reduce(
    (acc, o, j) => (o === 'lost' ? acc.plus(stake[j]!) : acc),
    ZERO,
  );
  const shares = outcomes.map((outcome, i) => {
    if (outcome === 'lost') return stake[i]!.neg().toDecimalPlaces(2);
    if (outcome === 'won') return total.plus(lost).toDecimalPlaces(2);
    return ZERO;
  });
  if (!shares.reduce((a, b) => a.plus(b), ZERO).eq(total)) {
    const fallback = raw.map((p) => p.toDecimalPlaces(2));
    total = fallback.reduce((a, b) => a.plus(b), ZERO);
    return { shares: fallback, total };
  }
  return { shares, total };
}

function splitLine(line: Decimal): [Decimal, Decimal] | null {
  const frac = line.abs().mod(1);
  if (!frac.eq(QUARTER) && !frac.eq(new Decimal('0.75'))) return null;
  const sign = line.gte(0) ? ONE : new Decimal(-1);
  return [sign.times(line.abs().minus(QUARTER)), sign.times(line.abs().plus(QUARTER))];
}

function halfLine(margin: Decimal, line: Decimal): string {
  const adj = margin.plus(line);
  if (adj.gt(0)) return 'won';
  if (adj.lt(0)) return 'lost';
  return 'push';
}

function combineHalves(a: string, b: string): string {
  const pair = new Set([a, b]);
  if (pair.size === 1 && a === 'won') return 'won';
  if (pair.size === 1 && a === 'lost') return 'lost';
  if (pair.size === 1 && a === 'push') return 'push';
  if (pair.has('won') && pair.has('push')) return 'half_win';
  if (pair.has('lost') && pair.has('push')) return 'half_loss';
  return 'lost';
}

export function handicapOutcome(line: Money, margin: Money): string {
  const lineD = d(line);
  const halves = splitLine(lineD);
  if (halves === null) return halfLine(d(margin), lineD);
  return combineHalves(halfLine(d(margin), halves[0]), halfLine(d(margin), halves[1]));
}

export function countsAsWin(outcome: string): boolean | null {
  if (WIN_RATE_SKIP.has(outcome)) return null;
  if (outcome === 'won') return true;
  if (outcome === 'lost' || outcome === 'rejected') return false;
  return null;
}

export function asPlain(value: Decimal): string {
  return value.toFixed(2);
}

export { CENT };
